import { useMemo, useState } from 'react';
import { Copy, Download, Eraser, RefreshCw, ShieldAlert } from 'lucide-react';
import { useCleanStart, useCleanStartSummary, type CleanStartResult } from '@/api/hooks/cleanStart';
import { Badge } from '@/components/ui/Badge';
import { Button, IconButton } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { Checkbox, Field, Input } from '@/components/ui/Field';
import { LoadingBlock } from '@/components/ui/State';
import { toast } from '@/stores/toast';

const CONFIRM = 'ИЗЧИСТИ';

type Role = 'dispatcher' | 'accountant' | 'mechanic';
const ROLES: { role: Role; title: string; email: string }[] = [
  { role: 'dispatcher', title: 'Диспечер', email: 'dispecher@kmd-express.com' },
  { role: 'accountant', title: 'Счетоводител', email: 'schetovodstvo@kmd-express.com' },
  { role: 'mechanic', title: 'Майстор', email: 'maystor@kmd-express.com' },
];

const REMOVE_LABELS: Record<string, string> = {
  orders: 'заявки',
  trips: 'курса',
  documents: 'документа и файла',
  invoices: 'фактури',
  expenses: 'разхода',
  fuel: 'зареждания',
  drivers: 'шофьори',
  partners: 'клиенти и доставчици',
  places: 'места',
  vehicles: 'примерни буса',
  users: 'профила',
  deadlines: 'срока и винетки',
};

/**
 * Парола от браузъра — сървърът я получава само за да я запише хеширана.
 * 16 знака без объркващи (0/O, 1/l/I), винаги с буква и цифра.
 */
function generatePassword(): string {
  const letters = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ';
  const digits = '23456789';
  const all = letters + digits;
  const random = new Uint32Array(16);
  crypto.getRandomValues(random);
  const chars = [...random].map((n) => all[n % all.length]!);
  chars[random[0]! % 8] = letters[random[1]! % letters.length]!;
  chars[8 + (random[2]! % 8)] = digits[random[3]! % digits.length]!;
  return chars.join('');
}

/**
 * ЧИСТ СТАРТ — бутонът, с който пробата свършва и започват истинските данни.
 * Само за управителя; иска паролата му и думата „ИЗЧИСТИ".
 */
export function CleanStartCard() {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<{ data: CleanStartResult; passwords: { title: string; email: string; password: string }[] } | null>(null);
  return (
    <Card className="border-danger/40">
      <CardHeader
        title={
          <span className="flex items-center gap-1.5 text-danger">
            <ShieldAlert className="size-4" />
            Чист старт — махане на примерните данни
          </span>
        }
        subtitle="Остават фирмата, настройките, твоят профил, Frotcom и бусовете от Frotcom. Всичко останало от пробата се изтрива."
        actions={
          <Button variant="danger" icon={<Eraser className="size-4" />} onClick={() => setOpen(true)}>
            Чист старт
          </Button>
        }
      />
      {result ? (
        <CardBody>
          <CreatedAccounts result={result} />
        </CardBody>
      ) : null}
      {open ? (
        <CleanStartDialog
          onClose={() => setOpen(false)}
          onDone={(data, passwords) => {
            setOpen(false);
            setResult({ data, passwords });
          }}
        />
      ) : null}
    </Card>
  );
}

function CleanStartDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (data: CleanStartResult, passwords: { title: string; email: string; password: string }[]) => void;
}) {
  const summary = useCleanStartSummary(true);
  const run = useCleanStart();
  const [accounts, setAccounts] = useState(() => ROLES.map((r) => ({ ...r, on: true, password: generatePassword() })));
  const [driver, setDriver] = useState(true);
  const [renameOwner, setRenameOwner] = useState(true);
  const [confirm, setConfirm] = useState('');
  const [password, setPassword] = useState('');
  const chosen = accounts.filter((a) => a.on);
  const emailsOk = chosen.every((a) => /.+@.+\..+/.test(a.email.trim()));
  const ready = confirm.trim() === CONFIRM && password.length > 0 && emailsOk;

  const removeLine = useMemo(() => {
    const remove = summary.data?.remove ?? {};
    return Object.entries(remove)
      .filter(([, n]) => n > 0)
      .map(([key, n]) => `${n} ${REMOVE_LABELS[key] ?? key}`)
      .join(', ');
  }, [summary.data]);

  const submit = () =>
    run.mutate(
      {
        confirm: confirm.trim(),
        password,
        ...(renameOwner ? { ownerName: 'Управител' } : {}),
        accounts: chosen.map((a) => ({ role: a.role, email: a.email.trim().toLowerCase(), password: a.password })),
        driver,
      },
      {
        onSuccess: (data) => {
          toast.ok('Примерните данни са изтрити', 'Системата е готова за истинските данни.');
          onDone(
            data,
            chosen
              .filter((a) => data.created.some((c) => c.role === a.role))
              .map((a) => ({ title: a.title, email: a.email.trim().toLowerCase(), password: a.password })),
          );
        },
        onError: (error) => toast.error('Нищо не е изтрито', error instanceof Error ? error.message : undefined),
      },
    );

  return (
    <Dialog
      open
      onClose={onClose}
      width="xl"
      title="Чист старт"
      description="Изтриването е необратимо. На сървъра има резервно копие на базата от последното качване."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button variant="danger" icon={<Eraser className="size-4" />} loading={run.isPending} disabled={!ready} onClick={submit}>
            Изтрий примерните данни
          </Button>
        </>
      }
    >
      {summary.isLoading ? (
        <LoadingBlock />
      ) : (
        <div className="space-y-5 text-sm">
          <section className="rounded-lg border border-danger/40 bg-danger-soft/30 p-3">
            <div className="font-medium text-danger">Ще се изтрие:</div>
            <p className="mt-1 text-ink-dim">{removeLine || 'няма примерни данни'}.</p>
            <div className="mt-3 font-medium text-ok">Остава:</div>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-ink-dim">
              <li>фирмата и настройките ѝ; твоят профил; дневникът на действията</li>
              <li>
                {summary.data?.keep.providers.length ? `връзката с ${summary.data.keep.providers.join(', ')}` : 'Frotcom още не е свързан'}
                {summary.data?.keep.vehicles.length
                  ? ` и ${summary.data.keep.vehicles.length} буса от него: ${summary.data.keep.vehicles.map((v) => `${v.fleetNo} ${v.plate}`).join(', ')}`
                  : ' — бусове от Frotcom още няма; внасят се след това от „Frotcom и връзки"'}
              </li>
              {summary.data?.keep.places.length ? <li>базата: {summary.data.keep.places.join(', ')}</li> : null}
            </ul>
          </section>

          <section>
            <h3 className="mb-1 font-semibold">Профили по роли — без имена</h3>
            <p className="mb-2 text-xs text-ink-dim">
              Паролите са направени в този браузър и се показват само на теб — запиши ги или свали файла след това. Имейлът
              е за вход; може да се смени.
            </p>
            <ul className="divide-y divide-line rounded-lg border border-line">
              {accounts.map((a, i) => (
                <li key={a.role} className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <Checkbox
                    label={<span className="w-28 font-medium text-ink">{a.title}</span>}
                    checked={a.on}
                    onChange={() => setAccounts((prev) => prev.map((x, j) => (j === i ? { ...x, on: !x.on } : x)))}
                  />
                  <Input
                    className="h-8 min-w-0 flex-1 font-mono text-xs"
                    value={a.email}
                    disabled={!a.on}
                    onChange={(e) => setAccounts((prev) => prev.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))}
                  />
                  <span className="font-mono text-xs text-ink-dim">{a.password}</span>
                  <IconButton
                    label="Нова парола"
                    disabled={!a.on}
                    onClick={() => setAccounts((prev) => prev.map((x, j) => (j === i ? { ...x, password: generatePassword() } : x)))}
                  >
                    <RefreshCw className="size-3.5" />
                  </IconButton>
                </li>
              ))}
            </ul>
            <div className="mt-2 space-y-1">
              <Checkbox
                label="Празен запис „Шофьор“ — за кода за телефона (от „Шофьори“)"
                checked={driver}
                onChange={(e) => setDriver(e.target.checked)}
              />
              <Checkbox label="Името на моя профил да стане „Управител“" checked={renameOwner} onChange={(e) => setRenameOwner(e.target.checked)} />
            </div>
            <p className="mt-2 text-xs text-ink-faint">
              Профил за клиентския портал се прави за всеки истински клиент — след като клиентът е въведен в „Контрагенти“.
            </p>
          </section>

          <section className="grid gap-3 sm:grid-cols-2">
            <Field label={`Напиши „${CONFIRM}“`} required>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" />
            </Field>
            <Field label="Твоята парола" required hint="Изтриването е необратимо — затова и паролата.">
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            </Field>
          </section>
        </div>
      )}
    </Dialog>
  );
}

function CreatedAccounts({ result }: { result: { data: CleanStartResult; passwords: { title: string; email: string; password: string }[] } }) {
  const { data, passwords } = result;
  const text = [
    'КМД ЕКСПРЕС — профили по роли',
    'Вход: https://kmdx-dispatch.ovyra.ai',
    '',
    ...passwords.map((p) => `${p.title}: ${p.email} / ${p.password}`),
    '',
    'При първия вход се настройва двуфакторен код — приложение като Google Authenticator.',
  ].join('\n');
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'kmdx-profili.txt';
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div className="space-y-3 text-sm">
      <div className="rounded-lg border border-ok/40 bg-ok-soft/30 p-3">
        <div className="font-medium text-ok">Готово — системата е чиста.</div>
        <p className="mt-1 text-xs text-ink-dim">
          Изтрити: {Object.values(data.deleted).reduce((a, b) => a + b, 0)} записа и {data.filesRemoved} от {data.files} файла.
          {data.driverId ? ' Добавен е празен шофьор — дай му код за телефона от „Шофьори“.' : ''}
        </p>
      </div>
      {passwords.length ? (
        <div className="rounded-lg border border-warn/40 bg-warn-soft/30 p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="font-medium">Новите профили — паролите се виждат само сега</span>
            <span className="ml-auto flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                icon={<Copy className="size-3.5" />}
                onClick={() => void navigator.clipboard.writeText(text).then(() => toast.ok('Копирано'))}
              >
                Копирай
              </Button>
              <Button size="sm" variant="secondary" icon={<Download className="size-3.5" />} onClick={download}>
                Свали
              </Button>
            </span>
          </div>
          <ul className="space-y-1 font-mono text-xs">
            {passwords.map((p) => (
              <li key={p.email}>
                <Badge tone="neutral">{p.title}</Badge> {p.email} · {p.password}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {data.failed.length ? (
        <div className="rounded-lg border border-danger/40 p-3 text-xs text-danger">
          Не са създадени: {data.failed.map((f) => `${f.email} — ${f.message}`).join('; ')}
        </div>
      ) : null}
    </div>
  );
}
